import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getComandas } from "../helpers/rutaComandas";

import "../css/dashboardRentabilidad.css";
import "../css/historicoViajes.css";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril",
  "Mayo", "Junio", "Julio", "Agosto",
  "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const CORTOS = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const texto = (v) => String(v ?? "").trim();

const idRelacion = (v) =>
  texto(v && typeof v === "object" ? v._id : v);

const formato = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    maximumFractionDigits: 1,
  });

function fechaDia(valor) {
  const coincidencia = texto(valor).match(/^(\d{4}-\d{2}-\d{2})/);
  if (!coincidencia) return "";

  const s = coincidencia[1];
  const d = new Date(`${s}T12:00:00`);

  if (
    Number.isNaN(d.getTime()) ||
    d.getFullYear() !== Number(s.slice(0, 4)) ||
    d.getMonth() + 1 !== Number(s.slice(5, 7)) ||
    d.getDate() !== Number(s.slice(8, 10))
  ) return "";

  return s;
}

function mensajeError(e) {
  if (typeof e === "string") return e;

  const mensaje = e?.message || e?.msg;

  return typeof mensaje === "string"
    ? mensaje
    : "No se pudo obtener el histórico de viajes.";
}

function opcionesRelacion(comandas, campo, campoNombre) {
  const mapa = new Map();

  comandas.forEach((c) => {
    const relacion = c[campo];
    const id = idRelacion(relacion);

    if (id && !mapa.has(id)) {
      mapa.set(id, {
        id,
        nombre: texto(relacion?.[campoNombre]) || "Sin nombre",
      });
    }
  });

  return [...mapa.values()].sort((a, b) =>
    a.nombre.localeCompare(b.nombre, "es")
  );
}

// Número completo + transporte = un viaje.
// Los sufijos se consideran distintos: 1874 y 1874,1.
function agruparViajes(comandas) {
  const grupos = new Map();

  comandas.forEach((c) => {
    const nro = texto(c.nroviaje);
    if (!nro) return;

    const transporteId = idRelacion(c.codtra);
    const clave = JSON.stringify([transporteId, nro]);
    const fecha = fechaDia(c.fecha);
    const choferId = idRelacion(c.codcho);

    if (!grupos.has(clave)) {
      grupos.set(clave, {
        clave,
        nroviaje: nro,
        transporteId,
        fecha: "",
        choferes: new Set(),
      });
    }

    const viaje = grupos.get(clave);

    if (fecha && (!viaje.fecha || fecha < viaje.fecha)) {
      viaje.fecha = fecha;
    }

    if (choferId) viaje.choferes.add(choferId);
  });

  return [...grupos.values()];
}

function periodoInicial(anio, anioActual, mesActual) {
  const desde = Number(anio) === 2026 ? 2 : 0;
  const hasta = Number(anio) === anioActual ? mesActual : 11;

  return {
    desde,
    hasta: Math.max(desde, hasta),
  };
}

function Kpi({ titulo, valor, nota, color = "#2563eb" }) {
  return (
    <div className="dr-kpi" style={{ "--dr-accent": color }}>
      <div className="dr-kpi-label">{titulo}</div>
      <div className="dr-kpi-value">{valor}</div>
      <div className="dr-muted">{nota}</div>
    </div>
  );
}

export default function HistoricoViajes() {
  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const mesActual = hoy.getMonth();
  const fechaHoy = hoy.toLocaleDateString("es-AR");

  const [comandas, setComandas] = useState([]);
  const [anio, setAnio] = useState(String(anioActual));

  const [desdeMes, setDesdeMes] = useState(
    () => periodoInicial(anioActual, anioActual, mesActual).desde
  );

  const [hastaMes, setHastaMes] = useState(
    () => periodoInicial(anioActual, anioActual, mesActual).hasta
  );

  const [choferId, setChoferId] = useState("");
  const [transporteId, setTransporteId] = useState("");
  const [mesSeleccionado, setMesSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [actualizado, setActualizado] = useState("");

  useEffect(() => {
    let activo = true;

    async function cargar() {
      setCargando(true);
      setError("");

      try {
        const respuesta = await getComandas();

        if (!respuesta?.ok) {
          throw new Error(
            mensajeError(respuesta?.error || respuesta?.msg)
          );
        }

        if (!Array.isArray(respuesta.comandas)) {
          throw new Error("La respuesta no contiene un listado válido.");
        }

        if (activo) {
          setComandas(respuesta.comandas);
          setActualizado(new Date().toLocaleString("es-AR"));
        }
      } catch (e) {
        if (activo) setError(mensajeError(e));
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargar();

    return () => { activo = false; };
  }, [revision]);

  const viajes = useMemo(
    () => agruparViajes(comandas),
    [comandas]
  );

  const anios = useMemo(() => {
    const valores = new Set([String(anioActual)]);

    viajes.forEach((v) => {
      if (v.fecha) valores.add(v.fecha.slice(0, 4));
    });

    return [...valores].sort((a, b) => Number(b) - Number(a));
  }, [viajes, anioActual]);

  const choferes = useMemo(
    () => opcionesRelacion(comandas, "codcho", "nombre"),
    [comandas]
  );

  const transportes = useMemo(
    () => opcionesRelacion(comandas, "codtra", "transporte"),
    [comandas]
  );

  const viajesFiltrados = useMemo(
    () => viajes.filter((v) =>
      (!choferId || v.choferes.has(choferId)) &&
      (!transporteId || v.transporteId === transporteId)
    ),
    [viajes, choferId, transporteId]
  );

  const sinFecha = useMemo(
    () => viajesFiltrados.filter((v) => !v.fecha).length,
    [viajesFiltrados]
  );

  const meses = useMemo(() => {
    const resultado = MESES.map((nombre, indice) => ({
      indice,
      nombre,
      corto: CORTOS[indice],
      cantidad: 0,
      futuro:
        Number(anio) > anioActual ||
        (Number(anio) === anioActual && indice > mesActual),
      parcial:
        Number(anio) === anioActual && indice === mesActual,
    }));

    viajesFiltrados.forEach((v) => {
      if (!v.fecha || v.fecha.slice(0, 4) !== anio) return;

      const indice = Number(v.fecha.slice(5, 7)) - 1;
      resultado[indice].cantidad++;
    });

    return resultado.filter(
      (m) => m.indice >= desdeMes && m.indice <= hastaMes
    );
  }, [
    viajesFiltrados,
    anio,
    anioActual,
    mesActual,
    desdeMes,
    hastaMes,
  ]);

  const resumen = useMemo(() => {
    const transcurridos = meses.filter((m) => !m.futuro);
    const completos = transcurridos.filter((m) => !m.parcial);

    const total = transcurridos.reduce((s, m) => s + m.cantidad, 0);
    const totalCompletos = completos.reduce((s, m) => s + m.cantidad, 0);

    const mejor = completos.reduce(
      (actual, m) =>
        !actual || m.cantidad > actual.cantidad ? m : actual,
      null
    );

    return {
      total,
      promedio: completos.length
        ? totalCompletos / completos.length
        : null,
      mejor: mejor?.cantidad ? mejor : null,
      completos: completos.length,
    };
  }, [meses]);

  const escala = useMemo(() => {
    const maximo = Math.max(
      1,
      ...meses.filter((m) => !m.futuro).map((m) => m.cantidad)
    );

    return Math.max(1, Math.ceil(maximo / 4)) * 4;
  }, [meses]);

  const seleccion = meses.find(
    (m) => m.indice === mesSeleccionado
  );

  const incluyeMesActual = meses.some((m) => m.parcial);
  const periodoTexto = `${MESES[desdeMes]} a ${MESES[hastaMes]} de ${anio}`;

  const choferNombre =
    choferes.find((c) => c.id === choferId)?.nombre ||
    "Todos los choferes";

  const transporteNombre =
    transportes.find((t) => t.id === transporteId)?.nombre ||
    "Todos los transportes";

  const cambiar = (setter, valor) => {
    setter(valor);
    setMesSeleccionado(null);
  };

  const cambiarAnio = (valor) => {
    const periodo = periodoInicial(valor, anioActual, mesActual);

    setAnio(valor);
    setDesdeMes(periodo.desde);
    setHastaMes(periodo.hasta);
    setMesSeleccionado(null);
  };

  const cambiarDesde = (valor) => {
    const mes = Number(valor);

    setDesdeMes(mes);
    setHastaMes((actual) => Math.max(actual, mes));
    setMesSeleccionado(null);
  };

  const restablecer = () => {
    cambiarAnio(String(anioActual));
    setChoferId("");
    setTransporteId("");
  };

  return (
    <main className="danlu-rentabilidad danlu-historico">
      <div className="dr-heading">
        <div>
          <h1>Histórico de viajes</h1>
          <div className="dr-muted">
            Evolución mensual de la cantidad de viajes
          </div>
        </div>

        <Link
          to="/HomeImpresion"
          className="btn btn-outline-secondary btn-sm"
        >
          Volver a informes
        </Link>
      </div>

      <section className="dr-panel">
        <div className="hv-filtros">
          <div>
            <label htmlFor="hv-anio">Año</label>
            <select
              id="hv-anio"
              className="form-select form-select-sm"
              value={anio}
              disabled={cargando}
              onChange={(e) => cambiarAnio(e.target.value)}
            >
              {anios.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="hv-desde-mes">Desde mes</label>
            <select
              id="hv-desde-mes"
              className="form-select form-select-sm"
              value={desdeMes}
              disabled={cargando}
              onChange={(e) => cambiarDesde(e.target.value)}
            >
              {MESES.map((mes, indice) => (
                <option key={mes} value={indice}>{mes}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="hv-hasta-mes">Hasta mes</label>
            <select
              id="hv-hasta-mes"
              className="form-select form-select-sm"
              value={hastaMes}
              disabled={cargando}
              onChange={(e) =>
                cambiar(setHastaMes, Number(e.target.value))
              }
            >
              {MESES.map((mes, indice) => (
                <option
                  key={mes}
                  value={indice}
                  disabled={indice < desdeMes}
                >
                  {mes}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="hv-chofer">Chofer</label>
            <select
              id="hv-chofer"
              className="form-select form-select-sm"
              value={choferId}
              disabled={cargando}
              onChange={(e) => cambiar(setChoferId, e.target.value)}
            >
              <option value="">Todos los choferes</option>
              {choferes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="hv-transporte">Transporte</label>
            <select
              id="hv-transporte"
              className="form-select form-select-sm"
              value={transporteId}
              disabled={cargando}
              onChange={(e) => cambiar(setTransporteId, e.target.value)}
            >
              <option value="">Todos los transportes</option>
              {transportes.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>

          <div className="dr-actions hv-acciones">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={restablecer}
              disabled={cargando}
            >
              Restablecer
            </button>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setRevision((r) => r + 1)}
              disabled={cargando}
            >
              Actualizar datos
            </button>
          </div>
        </div>

        {anio === "2026" && (
          <div className="dr-muted mt-2">
            En 2026 el período inicial comienza en marzo,
            cuando empezó el uso regular del sistema.
            Podés cambiar los meses para consultar otro período.
          </div>
        )}
      </section>

      {cargando && (
        <div className="dr-panel dr-empty" role="status">
          <span
            className="spinner-border spinner-border-sm me-2"
            aria-hidden="true"
          />
          Cargando histórico de viajes…
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!cargando && !error && (
        <>
          <div className="dr-toolbar">
            <div>
              <strong>
                {periodoTexto} · {choferNombre} · {transporteNombre}
              </strong>

              <div className="dr-muted">
                Los filtros se aplican automáticamente.
              </div>
            </div>

            <span className="dr-type">
              {comandas.length} comandas cargadas
            </span>
          </div>

          {sinFecha > 0 && (
            <div className="alert alert-warning py-2">
              Hay {sinFecha} viajes sin fecha válida para los filtros
              de chofer y transporte. No se pueden asignar a un año
              y están excluidos del informe.
            </div>
          )}

          <div className="dr-kpis hv-kpis" aria-live="polite">
            <Kpi
              titulo="Viajes del período"
              valor={formato(resumen.total)}
              nota={
                incluyeMesActual
                  ? `${periodoTexto} · parcial al ${fechaHoy}`
                  : periodoTexto
              }
            />

            <Kpi
              titulo="Promedio mensual"
              valor={
                resumen.promedio == null
                  ? "—"
                  : formato(resumen.promedio)
              }
              nota={`${resumen.completos} meses completos del período`}
              color="#0c8da5"
            />

            <Kpi
              titulo="Mes con más viajes"
              valor={resumen.mejor?.nombre || "—"}
              nota={
                resumen.mejor
                  ? `${formato(resumen.mejor.cantidad)} viajes · meses completos`
                  : "Sin viajes en meses completos"
              }
              color="#087e57"
            />
          </div>

          <section className="dr-panel">
            <div className="dr-toolbar">
              <div>
                <h2 className="mb-1">Cantidad de viajes por mes</h2>
                <div className="dr-muted">{periodoTexto}</div>
              </div>

              {incluyeMesActual && (
                <span className="dr-type">
                  {MESES[mesActual]}: mes en curso
                </span>
              )}
            </div>

            <div className="hv-grafico-scroll">
              <div className="hv-grafico">
                <div className="hv-eje-titulo">
                  Cantidad de viajes
                </div>

                <div className="hv-plot">
                  <div className="hv-eje-y" aria-hidden="true">
                    {[4, 3, 2, 1, 0].map((n) => (
                      <span key={n}>
                        {formato(escala * n / 4)}
                      </span>
                    ))}
                  </div>

                  <div
                    className="hv-barras"
                    style={{
                      gridTemplateColumns:
                        `repeat(${meses.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {meses.map((m) => (
                      <div className="hv-columna" key={m.indice}>
                        <button
                          type="button"
                          className={[
                            "hv-mes",
                            m.parcial ? "hv-parcial" : "",
                            mesSeleccionado === m.indice
                              ? "hv-seleccionado"
                              : "",
                          ].join(" ")}
                          disabled={m.futuro}
                          aria-pressed={mesSeleccionado === m.indice}
                          aria-label={`${m.nombre}: ${
                            m.futuro
                              ? "mes futuro"
                              : `${m.cantidad} viajes${
                                  m.parcial ? ", mes en curso" : ""
                                }`
                          }`}
                          onClick={() => setMesSeleccionado(m.indice)}
                        >
                          <span className="hv-valor">
                            {m.futuro ? "—" : formato(m.cantidad)}
                          </span>

                          <span
                            className="hv-barra"
                            style={{
                              height: m.futuro
                                ? 0
                                : `${m.cantidad / escala * 100}%`,
                            }}
                          />
                        </button>

                        <span className="hv-mes-nombre">
                          {m.corto}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hv-eje-x">Mes</div>
              </div>
            </div>

            <div className="hv-leyenda">
              <span>
                <i className="hv-muestra" aria-hidden="true" />
                Mes completo
              </span>

              {incluyeMesActual && (
                <span>
                  <i
                    className="hv-muestra hv-muestra-parcial"
                    aria-hidden="true"
                  />
                  Mes en curso
                </span>
              )}
            </div>

            <div className="hv-detalle" aria-live="polite">
              {seleccion
                ? `${seleccion.nombre} ${anio}: ${
                    formato(seleccion.cantidad)
                  } viajes${
                    seleccion.parcial
                      ? ` · parcial al ${fechaHoy}`
                      : ""
                  }.`
                : "Seleccioná una barra para ver la cantidad de viajes del mes."}
            </div>
          </section>

          <section className="dr-panel">
            <h2>Detalle mensual</h2>

            <div className="table-responsive">
              <table className="table table-sm dr-table hv-tabla">
                <thead>
                  <tr>
                    <th scope="col">Mes</th>
                    <th scope="col" className="dr-num">Viajes</th>
                    <th scope="col" className="dr-num">
                      % del acumulado
                    </th>
                    <th scope="col">Período</th>
                  </tr>
                </thead>

                <tbody>
                  {meses.map((m) => (
                    <tr
                      key={m.indice}
                      className={
                        mesSeleccionado === m.indice
                          ? "hv-fila-activa"
                          : ""
                      }
                    >
                      <td>{m.nombre}</td>

                      <td className="dr-num">
                        {m.futuro ? "—" : formato(m.cantidad)}
                      </td>

                      <td className="dr-num">
                        {m.futuro || !resumen.total
                          ? "—"
                          : `${formato(
                              m.cantidad / resumen.total * 100
                            )} %`}
                      </td>

                      <td>
                        {m.futuro
                          ? "Mes futuro"
                          : m.parcial
                          ? "En curso"
                          : "Completo"}
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr>
                    <td>Total del período</td>
                    <td className="dr-num">
                      {formato(resumen.total)}
                    </td>
                    <td className="dr-num">
                      {resumen.total ? "100 %" : "—"}
                    </td>
                    <td>{anio}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <details className="dr-panel">
            <summary className="hv-summary">
              Cómo se cuenta cada viaje
            </summary>

            <div className="dr-muted mt-3">
              <p>
                Se cuenta una vez cada combinación de número completo
                de viaje y transporte. Si se repite en varias comandas,
                se utiliza su primera fecha válida.
              </p>

              <p>
                Los números con sufijo se consideran distintos:
                por ejemplo, 1874 y 1874,1 cuentan como dos viajes.
                El filtro de chofer incluye un viaje si alguna de sus
                comandas tiene ese chofer.
              </p>

              <p>
                El gráfico, los totales y el promedio corresponden
                exclusivamente al período seleccionado.
                El promedio incluye los meses completos de ese período,
                también aquellos sin viajes registrados.
              </p>

              <p>
                El mes en curso participa del acumulado, pero no del
                promedio ni de la selección del mejor mes.
                Los meses futuros se muestran sin datos y no participan
                de los cálculos.
              </p>

              <p>
                En 2026 la selección inicial comienza en marzo.
                Enero y febrero siguen disponibles si se seleccionan
                expresamente.
              </p>

              <p className="mb-0">
                El informe utiliza las comandas devueltas por la consulta
                actual. Última actualización: {actualizado}.
              </p>
            </div>
          </details>
        </>
      )}
    </main>
  );
}